<template>
  <div class="form-step">
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Projects ({{ localForm.projects.length }})</h5>
      <button type="button" class="btn btn-outline-primary" @click="addProject">
        <i class="bi bi-plus me-2"></i>Add Project
      </button>
    </div>

    <div v-for="(project, index) in localForm.projects || []" :key="projectKey(project, index)" class="project-item mb-4 p-3 border rounded">
      <div
        class="project-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="toggleProject(projectKey(project, index))"
        @keydown.enter.prevent="toggleProject(projectKey(project, index))"
        @keydown.space.prevent="toggleProject(projectKey(project, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ projectHeaderTitles[projectKey(project, index)] }}</h6>
        <i class="bi" :class="isProjectExpanded(projectKey(project, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Title</label>
          <input
            type="text"
            class="form-control"
            v-model="project.title"
            placeholder="Project Title"
            @input="updateParent"
          >
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start date</label>
          <input
            type="date"
            class="form-control"
            v-model="project.startDate"
            @input="updateParent"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End date</label>
          <input
            type="date"
            class="form-control"
            v-model="project.endDate"
            @input="updateParent"
          >
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">Collaborators</h6>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="addCollaborator(index)">
          <i class="bi bi-plus me-2"></i>ADD
        </button>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" v-for="(collaborator, collabIndex) in project.collaborators || []" :key="collaborator._id" class="collaborator-item mb-3 p-2 border rounded bg-light">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">Collaborator {{ collabIndex + 1 }}</small>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeCollaborator(index, collabIndex)">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="row mb-2">
          <div class="col-md-4">
            <label class="form-label">Name</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="collaborator.department"
              @input="updateParent"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label">Institution</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="collaborator.institution"
              @input="updateParent"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label">Country</label>
            <select
              class="form-select form-select-sm"
              v-model="collaborator.country"
              @change="updateParent"
            >
              <option value="">Select Country</option>
              <option v-for="country in africanCountries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">Sites</h6>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="addSite(index)">
          <i class="bi bi-plus me-2"></i>ADD
        </button>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" v-for="(site, siteIndex) in project.sites || []" :key="site._id" class="site-item mb-3 p-2 border rounded bg-light">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">Site {{ siteIndex + 1 }}</small>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeSite(index, siteIndex)">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="row mb-2">
          <div class="col-md-12">
            <label class="form-label">Name</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="site.name"
              placeholder="Site name"
              @input="updateParent"
            >
          </div>
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Subjects</label>
          <textarea
            class="form-control"
            rows="3"
            v-model="project.subjects"
            placeholder="Describe the subjects or participants"
            @input="updateParent"
          ></textarea>
        </div>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Outcomes</label>
          <textarea
            class="form-control"
            rows="3"
            v-model="project.outcomes"
            placeholder="Describe the project outcomes"
            @input="updateParent"
          ></textarea>
        </div>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Objectives</label>
          <textarea
            class="form-control"
            rows="3"
            v-model="project.objectives"
            placeholder="Project objectives"
            @input="updateParent"
          ></textarea>
        </div>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Year</label>
          <input
            type="number"
            class="form-control"
            v-model.number="project.year"
            :min="1900"
            :max="2100"
            placeholder="2025"
            @input="updateParent"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Type</label>
          <input
            type="text"
            class="form-control"
            v-model="project.type"
            placeholder="Research, Clinical, etc."
            @input="updateParent"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Code</label>
          <input
            type="text"
            class="form-control"
            v-model="project.code"
            placeholder="PROJECT001"
            @input="updateParent"
          >
        </div>
      </div>
      <div v-if="isProjectExpanded(projectKey(project, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Grant</label>
          <select
            class="form-select"
            v-model="project.grant"
            @change="updateParent"
          >
            <option value="">Select Grant</option>
            <option
              v-for="(grant, grantIndex) in availableGrants"
              :key="`grant-${grantIndex}`"
              :value="grant.code || grant.title"
            >
              {{ grant.title }} {{ grant.code ? `(${grant.code})` : '' }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="isProjectExpanded(projectKey(project, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end">
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeProject(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Show message when no projects -->
    <div v-if="localForm.projects.length === 0" class="text-center py-4 text-muted">
      <i class="bi bi-folder2 fs-1 mb-3"></i>
      <p>No projects added yet. Click the ADD button to add your first project.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectsForm',
  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    }
  },
  emits: ['update'],
  data() {
    return {
      localForm: { projects: [] },
      expandedProjectIds: [],
      initialized: false,
      showDebug: false, // Set to true for debugging
      africanCountries: [
        { value: 'algeria', label: 'Algeria' },
        { value: 'angola', label: 'Angola' },
        { value: 'benin', label: 'Benin' },
        { value: 'botswana', label: 'Botswana' },
        { value: 'burkina-faso', label: 'Burkina Faso' },
        { value: 'burundi', label: 'Burundi' },
        { value: 'cameroon', label: 'Cameroon' },
        { value: 'cape-verde', label: 'Cape Verde' },
        { value: 'central-african-republic', label: 'Central African Republic' },
        { value: 'chad', label: 'Chad' },
        { value: 'comoros', label: 'Comoros' },
        { value: 'congo', label: 'Congo' },
        { value: 'democratic-republic-congo', label: 'Democratic Republic of Congo' },
        { value: 'djibouti', label: 'Djibouti' },
        { value: 'egypt', label: 'Egypt' },
        { value: 'equatorial-guinea', label: 'Equatorial Guinea' },
        { value: 'eritrea', label: 'Eritrea' },
        { value: 'eswatini', label: 'Eswatini' },
        { value: 'ethiopia', label: 'Ethiopia' },
        { value: 'gabon', label: 'Gabon' },
        { value: 'gambia', label: 'Gambia' },
        { value: 'ghana', label: 'Ghana' },
        { value: 'guinea', label: 'Guinea' },
        { value: 'guinea-bissau', label: 'Guinea-Bissau' },
        { value: 'ivory-coast', label: 'Ivory Coast' },
        { value: 'kenya', label: 'Kenya' },
        { value: 'lesotho', label: 'Lesotho' },
        { value: 'liberia', label: 'Liberia' },
        { value: 'libya', label: 'Libya' },
        { value: 'madagascar', label: 'Madagascar' },
        { value: 'malawi', label: 'Malawi' },
        { value: 'mali', label: 'Mali' },
        { value: 'mauritania', label: 'Mauritania' },
        { value: 'mauritius', label: 'Mauritius' },
        { value: 'morocco', label: 'Morocco' },
        { value: 'mozambique', label: 'Mozambique' },
        { value: 'namibia', label: 'Namibia' },
        { value: 'niger', label: 'Niger' },
        { value: 'nigeria', label: 'Nigeria' },
        { value: 'rwanda', label: 'Rwanda' },
        { value: 'sao-tome-and-principe', label: 'São Tomé and Príncipe' },
        { value: 'senegal', label: 'Senegal' },
        { value: 'seychelles', label: 'Seychelles' },
        { value: 'sierra-leone', label: 'Sierra Leone' },
        { value: 'somalia', label: 'Somalia' },
        { value: 'south africa', label: 'South Africa' },
        { value: 'south sudan', label: 'South Sudan' },
        { value: 'sudan', label: 'Sudan' },
        { value: 'tanzania', label: 'Tanzania' },
        { value: 'togo', label: 'Togo' },
        { value: 'tunisia', label: 'Tunisia' },
        { value: 'uganda', label: 'Uganda' },
        { value: 'zambia', label: 'Zambia' },
        { value: 'zimbabwe', label: 'Zimbabwe' }
      ]
    }
  },
  computed: {
    projectHeaderTitles() {
      return (this.localForm.projects || []).reduce((titles, project, index) => {
        const key = this.projectKey(project, index)
        const code = this.normalizeHeaderText(project?.code).toUpperCase()
        const title = this.normalizeHeaderText(project?.title)
        const year = this.getYear(project?.year || project?.startDate)

        if (!code && !title && !year) {
          titles[key] = 'New Project'
          return titles
        }

        const left = [code, title].filter(Boolean).join(' | ')
        titles[key] = [left, year].filter(Boolean).join(', ')
        return titles
      }, {})
    },
    fellowDetailsProjects() {
      const projects = this.profileData?.data?.user?.fellow_details?.current_projects
      try {
        if (typeof projects === 'string') {
          const parsed = JSON.parse(projects)
          return Array.isArray(parsed) ? parsed : []
        } else if (Array.isArray(projects)) {
          return projects
        }
      } catch (e) {
        console.warn('Error parsing projects:', e)
      }
      return []
    },
    availableGrants() {
      // First try to get from formData (current session)
      if (this.formData?.grants && Array.isArray(this.formData.grants)) {
        return this.formData.grants.filter(grant => grant.title || grant.code)
      }

      // Fallback to profileData (saved grants)
      const fellowDetailsGrants = this.profileData?.data?.user?.fellow_details?.grants
      if (Array.isArray(fellowDetailsGrants) && fellowDetailsGrants.length > 0) {
        return fellowDetailsGrants.filter(grant => grant.title || grant.code)
      }

      return []
    }
  },
  watch: {
    formData: {
      handler(newData) {
        if (!newData || this.initialized) return

        if (newData.projects?.length) {
          Object.assign(this.localForm, this.initializeLocalForm(newData))
          this.initializeExpandedProjectIds()
          this.initialized = true
        }
      },
      deep: false
    },

    // ⭐ ADD THIS
    profileData: {
      handler(newData) {
        if (!newData || this.initialized) return

        const dbProjects = newData?.data?.user?.projects

        if (dbProjects?.length) {
          const projects = this.mapDatabaseProjects(dbProjects)

          console.log("PROJECTS FROM PROFILE:", projects)

          Object.assign(this.localForm, this.initializeLocalForm({ projects }))
          this.initializeExpandedProjectIds()

          this.initialized = true // ⭐ lock initialization
        }
      },
      immediate: true
    }
  },
  methods: {
    makeId(prefix = '') {
      const now = Number(Date.now()); // ensure it’s a number
      const random = Math.floor(Math.random() * 10000);
      return `${prefix}${now.toString(36)}-${random}`;
    },


    initializeLocalForm(data = null) {
      const sourceData = data || this.formData || {};
      const projectsSource = Array.isArray(sourceData.projects) ? sourceData.projects : [];

      return {
        projects: projectsSource.map(project => {
          const normalizedCollaborators = this.normalizeCollaborators(project.collaborators)

          return {
            _id: project._id || this.makeId('proj-'),
            _isNew: false,
            title: project.title || '',
            startDate: project.startDate || '',
            endDate: project.endDate || '',
            collaborators: normalizedCollaborators,
            sites: (Array.isArray(project.sites) ? project.sites : []).map(s => ({
              _id: s._id || this.makeId('site-'),
              name: s.name || ''
            })),
            subjects: project.subjects || '',
            outcomes: project.outcomes || '',
            objectives: project.objectives || '',
            year: project.year || new Date().getFullYear(),
            type: project.type || '',
            code: project.code || '',
            grant: project.grant || ''
          }
        })
      }
    },

    normalizeCollaborators(collaborators) {
      try {
        if (!collaborators) return []

        if (typeof collaborators === 'string') {
          collaborators = JSON.parse(collaborators)
        }

        if (!Array.isArray(collaborators)) return []

        return collaborators.map(c => ({
          _id: c?._id || this.makeId('coll-'),
          department: c?.department || c?.name || '',
          institution: c?.institution || c?.Title || '',
          country: c?.country || ''
        }))
      } catch (e) {
        console.warn('Error parsing collaborators', e)
        return []
      }
    },

    updateParent() {
      this.$emit('update', {
        ...this.localForm,
        projects: (this.localForm.projects || []).map(({ _isNew, ...project }) => ({ ...project }))
      })
    },

    addProject() {
      const newProject = {
        _id: this.makeId('proj-'),
        _isNew: true,
        title: '',
        startDate: '',
        endDate: '',
        collaborators: [],
        sites: [],
        subjects: '',
        outcomes: '',
        objectives: '',
        year: new Date().getFullYear(),
        type: '',
        code: '',
        grant: ''
      };

      this.localForm.projects.push(newProject);
      const key = this.projectKey(newProject, this.localForm.projects.length - 1)
      if (key && !this.expandedProjectIds.includes(key)) {
        this.expandedProjectIds.push(key)
      }
      this.updateParent();
    },

    removeProject(index) {
      const removed = this.localForm.projects[index]
      this.localForm.projects.splice(index, 1)
      const removedKey = this.projectKey(removed, index)
      this.expandedProjectIds = this.expandedProjectIds.filter((id) => id !== removedKey)
      this.updateParent()
    },

    projectKey(project, index) {
      return project?._id || `project-${index}`
    },

    toggleProject(key) {
      if (!key) return

      if (this.expandedProjectIds.includes(key)) {
        this.expandedProjectIds = this.expandedProjectIds.filter((id) => id !== key)
      } else {
        this.expandedProjectIds.push(key)
      }
    },

    isProjectExpanded(key) {
      return this.expandedProjectIds.includes(key)
    },

    initializeExpandedProjectIds() {
      this.expandedProjectIds = (this.localForm.projects || [])
        .map((project, index) => (project._isNew ? this.projectKey(project, index) : null))
        .filter(Boolean)
    },

    normalizeHeaderText(value) {
      if (typeof value !== 'string') return ''
      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    getYear(value) {
      if (value === null || value === undefined) return ''
      const asString = String(value).trim()
      const match = asString.match(/^(\d{4})/)
      return match ? match[1] : ''
    },

    addCollaborator(projectIndex) {
      this.localForm.projects[projectIndex].collaborators.push({
        _id: this.makeId('coll-'),
        department: '',
        institution: '',
        country: ''
      });
      this.updateParent();
    },

    removeCollaborator(projectIndex, collabIndex) {
      this.localForm.projects[projectIndex].collaborators.splice(collabIndex, 1)
      this.updateParent()
    },

    addSite(projectIndex) {
      this.localForm.projects[projectIndex].sites.push({
        _id: this.makeId('site-'),
        name: ''
      });
      this.updateParent();
    },

    removeSite(projectIndex, siteIndex) {
      this.localForm.projects[projectIndex].sites.splice(siteIndex, 1)
      this.updateParent()
    },

    mapDatabaseProjects(projects) {
      return projects.map(p => ({
        _id: p.id || this.makeId('proj-'),
        title: p.project_title || '',
        startDate: p.start_date || '',
        endDate: p.end_date || '',

        collaborators: this.normalizeCollaborators(p.collaborators),

        sites: this.normalizeSites(p.sites),

        subjects: p.subjects || '',
        outcomes: p.outcomes || '',
        objectives: p.objectives || '',
        year: p.year || new Date().getFullYear(),
        type: p.type || '',
        code: p.code || '',
        grant: p.grant_id || ''
      }))
    },

    normalizeSites(sites) {
      try {
        if (!sites) return []

        if (typeof sites === 'string') {
          sites = JSON.parse(sites)
        }

        if (!Array.isArray(sites)) return []

        return sites.map(s => ({
          _id: s?._id || this.makeId('site-'),
          name: typeof s === 'string' ? s : s?.name || ''
        }))
      } catch (e) {
        return []
      }
    }
  },

  mounted() {
    if (this.profileData?.data?.user?.projects?.length) {
      const projects = this.mapDatabaseProjects(
        this.profileData.data.user.projects
      )

      Object.assign(this.localForm, this.initializeLocalForm({ projects }))
      this.initializeExpandedProjectIds()
    }

    this.initializeExpandedProjectIds()
  }

}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}
.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}
.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}
.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}
.project-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}
.collaborator-item,
.site-item {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
}
h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}
.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}
.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}
.form-control-sm,
.form-select-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}
.text-muted {
  font-size: 0.875rem;
}

.fs-1 {
  font-size: 3rem !important;
}

.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: 600;
  font-size: 1.1rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
